/**
 * Staff session provider — the seam between the desktop and browser sign-in
 * paths (Task 4092 step 1).
 *
 * `useGoogleAuthStore` drives the SignInGate and gates the whole app. It has
 * always resolved identity through Tauri `invoke`, which does not exist in a
 * browser. Rather than branch inside every store method, the store now depends
 * on this interface and the implementation is chosen once at module load.
 *
 * Deliberately narrow: this covers ONLY the three operations the auth store
 * needs. The other ~33 Tauri call sites in `src/` are Task 4093's problem —
 * that task owns the platform flag and stripping Tauri-touching code from the
 * browser bundle. This is not that abstraction, and should not grow into it.
 */

import type { StaffProfile } from '@/lib/tauri'
import { IS_TAURI } from '@/lib/platform'
import { disableAutoSelect } from './gis'
import { clearIdToken, loadIdToken } from './id-token-storage'

const VITE_GATEWAY_URL: string | undefined = import.meta.env.VITE_GATEWAY_URL

/**
 * True when running inside the Tauri desktop shell.
 *
 * Task 4093 replaced the original runtime `__TAURI_INTERNALS__` sniff with a
 * build-time constant, so the browser bundle can drop the desktop branches
 * rather than merely skip them. The definition now lives in `@/lib/platform`;
 * this re-export is kept because the auth feature and its tests import
 * `IS_TAURI` from here, and the 4092 notes warn against churn in this file.
 *
 * New code should import from `@/lib/platform` directly.
 */
export { IS_TAURI }

/**
 * Which environment the browser build is pointed at.
 *
 * Drives the header badge AND the synthetic operator id in
 * `features/auth/api.ts` (`op_<env>_<email>`), which is user-visible
 * attribution. It does NOT affect routing or auth — those come from
 * VITE_GATEWAY_URL and the credential respectively (PR #111 N5).
 *
 * The environment store defaults to 'prod' and is normally corrected by
 * `initialize()` with configs from the Rust layer. The browser has no Rust
 * layer and never calls it, so without this the header badge reads PROD no
 * matter which gateway is in use. An operator acting on dev data under a PROD
 * badge — or, worse, the reverse — is a genuine hazard.
 *
 * This is EXPLICIT config, not inferred from the gateway URL: Cloud Run
 * hostnames are opaque hashes (`platform-api-wrlwd2htzq-…` is dev,
 * `platform-api-xfpaxwrgsa-…` is prod) carrying no environment marker, so any
 * sniffing heuristic would silently mislabel — which is the exact failure this
 * function exists to prevent.
 *
 * Unset defaults to 'prod'. Mislabelling prod as dev is the more dangerous
 * direction, so an unconfigured build claims the environment demanding more
 * caution.
 */
export function browserEnvironmentName(): 'dev' | 'prod' {
  const raw = (import.meta.env?.VITE_ENVIRONMENT ?? '').trim().toLowerCase()
  if (raw === 'dev' || raw === 'prod') return raw
  if (raw !== '') {
    // A typo must be loud (PR #111 N6). An equality test against one literal
    // means `development`, `Dev`, or a trailing space all silently resolve to
    // 'prod' — a DEV build wearing a PROD badge, which is the exact
    // mislabelling this function exists to prevent.
    console.warn(
      `[env] VITE_ENVIRONMENT="${import.meta.env?.VITE_ENVIRONMENT}" is not "dev" or "prod"; ` +
        'defaulting to prod. The environment badge and operator id will say prod.',
    )
  }
  return 'prod'
}

/** The identity resolved from a successful sign-in. */
export interface StaffSession {
  /** Operator email. May be empty on a desktop fail-open network error. */
  email: string
  /**
   * Google ID token, browser only. The desktop path keeps its token in the Rust
   * layer and never exposes it to JS, so this is undefined there.
   */
  idToken?: string
}

export interface StaffSessionProvider {
  /**
   * Resolve an existing session without user interaction.
   * Returns null when there is no session — the caller renders the SignInGate.
   */
  restore(): Promise<StaffSession | null>
  /** Interactive sign-in, triggered by the operator clicking the gate. */
  signIn(): Promise<StaffSession>
  /** Best-effort teardown. Must not throw. */
  signOut(): Promise<void>
}

/**
 * Lazily resolve Tauri's `invoke` (Task 4093).
 *
 * Only ever reached from `tauriProvider`, which `staffSessionProvider` selects
 * on the desktop build alone. The dynamic import keeps `@tauri-apps/api/core`
 * out of the browser bundle: a static import here would be bundled regardless
 * of which provider is chosen at the bottom of this file, because bundlers
 * follow imports rather than call graphs.
 */
async function tauriCore() {
  const { invoke } = await import('@tauri-apps/api/core')
  return invoke
}

/**
 * Desktop: the Rust layer owns the OAuth flow and Keychain storage. Behaviour
 * here is unchanged from before this seam existed — the same three commands,
 * the same fail-open semantics on `check_google_auth`.
 */
const tauriProvider: StaffSessionProvider = {
  restore: async () => {
    const invoke = await tauriCore()
    const email = await invoke<string | null>('check_google_auth')
    // Empty string is a deliberate network-error fail-open from the Rust layer:
    // we are already authenticated on this machine, so unlock the app.
    return email === null ? null : { email: email || '' }
  },
  signIn: async () => {
    const invoke = await tauriCore()
    return { email: await invoke<string>('start_google_sign_in') }
  },
  signOut: async () => {
    try {
      const invoke = await tauriCore()
      await invoke('google_sign_out')
    } catch {
      // Clearing the Keychain entry must never block sign-out.
    }
  },
}

/**
 * Resolve a Google ID token to the staff member's email via the gateway's
 * bootstrap endpoint.
 *
 * Note the header scheme: `/v1/auth/staff/me` requires `GoogleIdToken <t>`,
 * NOT the `Bearer <t>` every other route uses. One token type, two schemes —
 * a wart recorded in ADR 0013, not an oversight here.
 *
 * A 404 means the account is a valid kodeon.ai Google identity with no
 * `staff_members` record. That is a real, actionable state ("ask an admin to
 * create your staff record"), so it is surfaced distinctly rather than folded
 * into a generic failure.
 */
export async function fetchStaffProfile(idToken: string): Promise<StaffProfile> {
  if (!VITE_GATEWAY_URL) {
    throw new Error('VITE_GATEWAY_URL is not set — cannot resolve staff profile')
  }

  const response = await fetch(`${VITE_GATEWAY_URL}/v1/auth/staff/me`, {
    headers: { Authorization: `GoogleIdToken ${idToken}` },
  })

  if (response.status === 404) {
    throw new Error('staff_not_found')
  }
  if (!response.ok) {
    throw new Error(`Staff profile lookup failed (${response.status})`)
  }

  const profile = (await response.json()) as StaffProfile
  if (!profile.email) {
    // Do NOT fall back to '' here (PR #109 N2). The desktop fail-open is
    // deliberate — the Rust layer returns '' when it is already authenticated
    // locally but offline — but the browser has no such justification. An empty
    // email would unlock the app into a half-signed-in state and hand
    // getOperatorIdentity() a garbage id derived from the ID token itself.
    throw new Error('Staff profile returned no email')
  }
  return profile
}

/**
 * Browser: Google Identity Services returns a short-lived ID token directly.
 *
 * `restore()` reuses the token persisted by the auth store (see
 * `id-token-storage.ts`) while it is unexpired, so a reload does not sign the
 * operator out. With no usable token it returns null at once and the gate
 * renders immediately.
 *
 * It deliberately does NOT attempt a silent One Tap here. That attempt can only
 * be settled by SILENT_PROMPT_TIMEOUT_MS under FedCM, so every signed-out load
 * sat on a splash for seconds before the gate appeared — and it rarely yielded
 * a credential anyway. The gate's personalised Google button is one click.
 * Silent One Tap is still used for the mid-session 401 refresh.
 */
const browserProvider: StaffSessionProvider = {
  restore: async () => {
    const idToken = loadIdToken()
    if (!idToken) return null

    try {
      return { email: (await fetchStaffProfile(idToken)).email, idToken }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      // The gateway rejected the stored token (revoked, key rotation, clock
      // skew past the expiry margin). Not an error the operator needs to see —
      // it is the same as having no session.
      if (message.includes('(401)')) {
        clearIdToken()
        return null
      }
      // A missing staff record, empty profile, or network failure IS worth
      // surfacing. The token is kept so a reload can retry a transient blip.
      throw err
    }
  },
  signIn: async () => {
    // Unreachable in the browser: SignInGate renders Google's own button and
    // calls completeBrowserSignIn with the credential directly, because
    // prompt() cannot be driven from a click (PR #111). Kept so the provider
    // interface stays uniform, and made explicit rather than silently
    // attempting a path that cannot work.
    throw new Error(
      'Browser sign-in goes through the Google-rendered button, not startSignIn',
    )
  },
  signOut: async () => {
    await disableAutoSelect()
  },
}

export const staffSessionProvider: StaffSessionProvider = IS_TAURI
  ? tauriProvider
  : browserProvider
